// 260715 Epic 3 Phase 1 — Chatbot trợ lý sửa Film (Drawer phải).
// Bot ĐỀ XUẤT op (thoại/action/prompt) → user bấm "Apply to draft" → đẩy vào nháp Epic 1 (chưa commit).
// 260722 copilot E2-P2: + action buttons (chạy pass/gen/lint…) — bấm-là-chạy qua filmApi, confirm theo bậc cost.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Input, Modal, Popconfirm, Space, Tooltip, Typography, Spin, Empty, message } from 'antd';
import { CaretRightOutlined, RobotOutlined, SendOutlined } from '@ant-design/icons';
import { filmApi, ChatAction } from '../../services/api/film';
import { resolveChatAction } from './filmChatActions';
import { apiErrorText } from './apiErrorText';
import type { BatchEditOp, EventPlan } from '../../types/film';
import type { WorkflowStepKey } from '../../types/filmWorkflow';
import { chatActionPlanOf, describeOpVi, splitStructOps } from './planToolsUtils';

const { Text, Paragraph } = Typography;

export type ChatMsg = { role: 'user' | 'assistant'; content: string; ops?: BatchEditOp[]; actions?: ChatAction[]; /** P9D: tin lỗi của trợ lý — hiện nút «Thử lại câu vừa rồi». */ error?: boolean };

/** 261010 P9D D9 — khung 8 bước: đề xuất áp THẲNG vào plan (batch-edit, không có nháp cấp plan), nút pass lẻ đổi thành «dẫn tới bước». Không truyền ⇒ UI cũ y nguyên. */
export interface FilmChatWorkflowMode {
  onApplyOps: (ops: BatchEditOp[]) => Promise<number>;
  /** Lý do khoá nút Áp dụng (lượt AI ghi plan đang chạy); null ⇒ mở. */
  applyBlock: string | null;
  onGoStep: (step: WorkflowStepKey) => void;
  /** run-lint / run-judge chạy ở tab «Chạy & chấm» của drawer. */
  onRunVerify: (kind: 'lint' | 'judge') => void;
}

interface Props {
  /** 261030 C1 — plan layout four: ẩn hành động «gen clip» của trợ lý (gen video ở bước 4 khung 4 bước). */
  layoutFour?: boolean;
  planId: string;
  view: EventPlan;                       // nháp hiện tại (để dịch eventId → nhãn "E{order}")
  onApplyOps: (ops: BatchEditOp[]) => number; // đẩy op vào nháp, trả số op áp được
  onActionDone?: () => void;             // action pipeline chạy xong → parent reload plan
  // E3-P1: phiên chat sống ở PARENT (EventPlanTab) → đóng/mở Drawer không mất lịch sử, không khám lại.
  messages: ChatMsg[];
  onMessagesChange: React.Dispatch<React.SetStateAction<ChatMsg[]>>;
  currentStage?: string;                 // stage user đang xem trên rail — ngữ cảnh cho câu mơ hồ
  workflow?: FilmChatWorkflowMode;
}

const FilmChatPanel: React.FC<Props> = ({ planId, view, onApplyOps, onActionDone, messages, onMessagesChange, currentStage, workflow, layoutFour }) => {
  const setMessages = onMessagesChange;
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [runningAction, setRunningAction] = useState<string | null>(null); // key `${msgIdx}:${actIdx}`
  const [checking, setChecking] = useState(false); // health-check mở màn (E2-P3)
  const listRef = useRef<HTMLDivElement>(null);

  // E2-P3: mở Drawer lần đầu (messages rỗng) → bản khám nhanh làm message mở màn.
  // Fail → im lặng về Empty cũ (health là phụ trợ, không chặn chat). Server cache theo modifiedAt.
  useEffect(() => {
    if (messages.length > 0 || checking) return;
    let alive = true;
    setChecking(true);
    filmApi.chatbotHealthCheck(planId)
      .then(({ reply, actions }) => {
        if (!alive || !reply) return;
        setMessages((prev) => (prev.length ? prev : [{
          role: 'assistant', content: reply, actions: actions && actions.length ? actions : undefined,
        }]));
      })
      .catch(() => undefined)
      .finally(() => { if (alive) setChecking(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  // eventId/shotId → "E{eventOrder}" để mô tả đề xuất cho dễ đọc.
  const { orderOf, shotOrderOf } = useMemo(() => {
    const ev = new Map<string, number>();
    const sh = new Map<string, number>();
    view.scenes.forEach((s) => s.events.forEach((e) => {
      ev.set(e.id, e.eventOrder);
      (e.shots || []).forEach((x) => sh.set(x.id, e.eventOrder));
    }));
    return { orderOf: ev, shotOrderOf: sh };
  }, [view]);

  const describeOp = (op: BatchEditOp): string => {
    if (workflow) return describeOpVi(op, orderOf, shotOrderOf);
    if (op.type === 'updateShot') return `Adjust camera of a shot in E${shotOrderOf.get(op.shotId) ?? '?'}`;
    // E4-P3: op cấu trúc (bot đề xuất) — mô tả tiếng Việt cho rõ hệ quả.
    if (op.type === 'splitEvent') return `✂️ Tách E${orderOf.get(op.eventId) ?? '?'} sau shot ${(op as any).keepShotCount}`;
    if (op.type === 'mergeNext') return `⬇️ Gộp E${orderOf.get(op.eventId) ?? '?'} với event kế tiếp`;
    if (op.type === 'insertAfter') return `＋ Chèn event trống sau E${orderOf.get(op.eventId) ?? '?'}`;
    if (op.type === 'deleteEvent') return `🗑 XOÁ E${orderOf.get(op.eventId) ?? '?'} (mất thoại + shot của event này)`;
    const ref = `E${orderOf.get(op.eventId) ?? '?'}`;
    if (op.type === 'replaceDialogue') return `Rewrite dialogue of ${ref} (${op.lines.length} line${op.lines.length > 1 ? 's' : ''})`;
    if (op.type === 'updateEvent') return `Rewrite ${[op.action !== undefined && 'action', op.kind !== undefined && 'shot count'].filter(Boolean).join(' + ')} of ${ref}`;
    if (op.type === 'updatePrompt') return `Rewrite prompt of ${ref}`;
    return 'Unsupported edit';
  };

  const scrollDown = () => setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' }), 30);

  const send = async (retryText?: string) => {
    const text = (retryText ?? input).trim();
    if (!text || loading) return;
    const history = [...(retryText !== undefined ? messages.slice(0, -2) : messages), { role: 'user' as const, content: text }]; // thử lại: bỏ tin lỗi + câu hỏi đã gửi
    setMessages(history.map((m) => ({ role: m.role, content: m.content, ops: (m as ChatMsg).ops })));
    setInput('');
    setLoading(true);
    scrollDown();
    try {
      const { reply, ops, actions } = await filmApi.chatbotSuggest(planId, history.map((m) => ({ role: m.role, content: m.content })), currentStage);
      setMessages((prev) => [...prev, {
        role: 'assistant', content: reply || '(no reply)',
        ops: ops && ops.length ? ops : undefined,
        actions: actions && actions.length ? actions : undefined,
      }]);
      scrollDown();
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Assistant failed');
      setMessages((prev) => [...prev, { role: 'assistant', content: '⚠ Xin lỗi, trợ lý gặp lỗi. Thử lại nhé.', error: true }]);
    } finally { setLoading(false); }
  };

  const [applying, setApplying] = useState(false);
  // P9D: khung mới — áp thẳng vào plan qua batch-edit (op cấu trúc không qua chat: có thanh «Cấu trúc video» ở bước 1).
  const applyToPlan = async (ops: BatchEditOp[]) => {
    if (!workflow) return;
    const { apply, skipped } = splitStructOps(ops);
    if (skipped.length) message.warning(`Bỏ qua ${skipped.length} thao tác cấu trúc (cắt / gộp / chèn / xoá video) — dùng thanh «Cấu trúc video» ở bước 1.`, 6);
    if (!apply.length) { if (!skipped.length) message.info('Không có gì để áp dụng.'); return; }
    setApplying(true);
    try {
      const n = await workflow.onApplyOps(apply);
      message.success(`Đã áp dụng ${n} đề xuất vào plan — bước sau của các video bị sửa có thể thành cũ.`);
    } catch (e: any) { message.error(apiErrorText(e, 'Áp dụng đề xuất')); }
    finally { setApplying(false); onActionDone?.(); }
  };
  const applyOps = (ops: BatchEditOp[]) => {
    const n = onApplyOps(ops);
    if (n > 0) message.success(`Applied ${n} suggestion${n > 1 ? 's' : ''} to your draft — review then Save.`);
    else message.info('Nothing to apply.');
  };

  // Chạy action pipeline (E2-P2). cost money đã confirm qua Modal ở nơi gọi; ai qua Popconfirm; free chạy thẳng.
  const runAction = async (key: string, act: ChatAction) => {
    const def = resolveChatAction(act.id, layoutFour);
    if (!def || runningAction) return;
    setRunningAction(key);
    try {
      await def.run(planId, act.eventId);
      // 260806: hầu hết action nay là việc NỀN (202) — nói "đã chạy" là sai, người dùng sẽ tưởng xong ngay.
      // Trạng thái thật xem ở băng "đang chạy" / băng đỏ của màn plan (GET /plans/:id/jobs).
      message.info(`${def.labelVi}: đã khởi động ở nền — theo dõi băng trạng thái, đừng bấm lại.`);
      onActionDone?.();
    } catch (e: any) {
      message.error(apiErrorText(e, def.labelVi));
      onActionDone?.();
    } finally { setRunningAction(null); }
  };

  const confirmAndRun = (key: string, act: ChatAction) => {
    const def = resolveChatAction(act.id, layoutFour);
    if (!def) return;
    if (def.cost === 'money') {
      Modal.confirm({
        title: def.labelVi,
        content: `Thao tác này TỐN TIỀN gen (video/ảnh)${actionEvLabel(act) ? ` — đích: ${actionEvLabel(act)}` : ''}. Chạy chứ?`,
        okText: 'Chạy', cancelText: 'Thôi',
        onOk: () => runAction(key, act),
      });
    } else {
      runAction(key, act); // free/ai: ai đã bọc Popconfirm ở nút
    }
  };

  const actionEvLabel = (act: ChatAction): string | null =>
    act.eventId ? `E${orderOf.get(act.eventId) ?? '?'}` : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '4px 2px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.length === 0 && checking && (
          <div style={{ alignSelf: 'flex-start', padding: '8px 4px' }}>
            <Spin size="small" /> <Text type="secondary" style={{ fontSize: 12 }}>Đang khám plan…</Text>
          </div>
        )}
        {messages.length === 0 && !checking && (
          <Empty image={<RobotOutlined style={{ fontSize: 40, color: '#A9762A' }} />}
            description={<div style={{ maxWidth: 320, margin: '0 auto', color: '#8a8a8a' }}>
              <Paragraph style={{ marginBottom: 6 }}>{workflow ? 'Kể cho trợ lý bạn muốn sửa gì — nó đề xuất, bạn bấm Áp dụng thì mới ghi vào plan.' : 'Kể cho trợ lý bạn muốn sửa gì — nó sẽ đề xuất thay đổi vào bản nháp để bạn duyệt.'}</Paragraph>
              <Text type="secondary" style={{ fontSize: 12 }}>Ví dụ: “Làm thoại event 3 tự nhiên hơn”, “Thêm một câu cho nhân vật chính ở event 5”, “Viết lại action event 2 cho kịch tính”.</Text>
            </div>} />
        )}
        {messages.map((m, i) => (
          <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '88%' }}>
            <div style={{
              background: m.role === 'user' ? '#A9762A' : '#f4f1ea',
              color: m.role === 'user' ? '#fff' : '#2b2b2b',
              border: m.role === 'user' ? 'none' : '1px solid #e7e0d2',
              borderRadius: 12, padding: '8px 12px', fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.5,
            }}>{m.content}</div>
            {workflow && m.error && i === messages.length - 1 && !loading && (
              <Button size="small" style={{ marginTop: 4 }} onClick={() => { const lastUser = [...messages].reverse().find((x) => x.role === 'user'); if (lastUser) void send(lastUser.content); }} data-testid="wf-chat-retry">Thử lại câu vừa rồi</Button>
            )}
            {m.role === 'assistant' && m.ops && m.ops.length > 0 && (
              <div style={{ marginTop: 6, background: '#fff', border: '1px solid #e7e0d2', borderRadius: 10, padding: 10 }}>
                <Text strong style={{ fontSize: 12 }}>{workflow ? 'Đề xuất sửa' : 'Proposed edits'}</Text>
                <ul style={{ margin: '6px 0 8px', paddingLeft: 18, fontSize: 12, color: '#555' }}>
                  {m.ops.map((op, j) => <li key={j}>{describeOp(op)}</li>)}
                </ul>
                {workflow ? (
                  <Tooltip title={workflow.applyBlock ?? undefined}>
                    <span>
                      <Popconfirm title="Áp dụng đề xuất vào plan?" okText="Áp dụng" cancelText="Thôi" disabled={!!workflow.applyBlock || applying}
                        description={<div style={{ maxWidth: 300 }}>Ghi thẳng vào plan (không có bản nháp). Bước sau của video bị sửa (prompt, clip, liền mạch) sẽ thành cũ.{m.ops.some((o) => o.type === 'deleteEvent') ? ' Đề xuất có thao tác XOÁ video — thao tác đó sẽ bị bỏ qua, xoá ở bước 1.' : ''}</div>}
                        onConfirm={() => { void applyToPlan(m.ops!); }}>
                        <Button size="small" type="primary" loading={applying} disabled={!!workflow.applyBlock || i !== messages.length - 1} data-testid="wf-chat-apply">Áp dụng vào plan…</Button>
                      </Popconfirm>
                    </span>
                  </Tooltip>
                ) : m.ops.some((o) => o.type === 'deleteEvent') ? (
                  // E4-P3: đề xuất có op XOÁ event (mất dữ liệu) → hỏi lại, dù nháp vẫn là lưới cuối.
                  <Popconfirm
                    title="Đề xuất có thao tác XOÁ event"
                    description="Event bị xoá kèm thoại + shot của nó (chưa mất hẳn tới khi bấm 💾 Lưu). Áp vào nháp?"
                    okText="Áp vào nháp" cancelText="Thôi" okButtonProps={{ danger: true }}
                    onConfirm={() => applyOps(m.ops!)}
                  >
                    <Button size="small" type="primary" danger>Apply to draft</Button>
                  </Popconfirm>
                ) : (
                  <Button size="small" type="primary" onClick={() => applyOps(m.ops!)}>Apply to draft</Button>
                )}
              </div>
            )}
            {m.role === 'assistant' && m.actions && m.actions.length > 0 && (
              <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {m.actions.map((act, j) => {
                  if (workflow) {
                    const plan = chatActionPlanOf(act.id);
                    const stale = i !== messages.length - 1;
                    if (plan.kind === 'GOTO') return <Tooltip key={`${i}:${j}`} title={act.reason}><Button size="small" disabled={stale} onClick={() => workflow.onGoStep(plan.step)} data-testid="wf-chat-goto">{plan.label}</Button></Tooltip>;
                    if (plan.kind === 'RUN') return <Tooltip key={`${i}:${j}`} title={stale ? 'Đề xuất cũ — hỏi lại để chạy' : act.reason}><Button size="small" icon={<CaretRightOutlined />} disabled={stale} onClick={() => workflow.onRunVerify(act.id === 'run-lint' ? 'lint' : 'judge')} data-testid="wf-chat-verify">{act.id === 'run-lint' ? 'Kiểm lỗi plan' : 'Chấm AI plan'}</Button></Tooltip>;
                    return <Tooltip key={`${i}:${j}`} title="Hành động mới — cần cập nhật UI"><span><Button size="small" disabled>{act.id}</Button></span></Tooltip>;
                  }
                  const def = resolveChatAction(act.id, layoutFour);
                  const key = `${i}:${j}`;
                  // Chỉ message CUỐI mới bấm được — action trên context cũ có thể đã lỗi thời.
                  const stale = i !== messages.length - 1;
                  const label = def ? `${def.labelVi}${act.eventId ? ` (E${orderOf.get(act.eventId) ?? '?'})` : ''}` : act.id;
                  const btn = (
                    <Button
                      size="small" icon={<CaretRightOutlined />}
                      loading={runningAction === key}
                      disabled={!def || stale || (!!runningAction && runningAction !== key)}
                      onClick={def && def.cost !== 'ai' ? () => confirmAndRun(key, act) : undefined}
                    >{label}</Button>
                  );
                  const wrapped = def && def.cost === 'ai' && !stale ? (
                    <Popconfirm key={key} title={`Chạy "${def.labelVi}"? (1 lượt AI)`} okText="Chạy" cancelText="Thôi" onConfirm={() => confirmAndRun(key, act)}>
                      {btn}
                    </Popconfirm>
                  ) : btn;
                  return (
                    <Tooltip key={key} title={!def ? 'Hành động mới — cần cập nhật UI' : stale ? 'Đề xuất cũ — hỏi lại để chạy' : act.reason}>
                      <span>{wrapped}</span>
                    </Tooltip>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        {loading && <div style={{ alignSelf: 'flex-start' }}><Spin size="small" /> <Text type="secondary" style={{ fontSize: 12 }}>Thinking…</Text></div>}
      </div>
      <div style={{ borderTop: '1px solid #eee', paddingTop: 10, marginTop: 8 }}>
        <Space.Compact style={{ width: '100%' }}>
          <Input.TextArea
            value={input} onChange={(e) => setInput(e.target.value)}
            placeholder="Nói bạn muốn sửa gì…"
            autoSize={{ minRows: 1, maxRows: 4 }}
            onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); void send(); } }}
            disabled={loading}
          />
          <Button type="primary" icon={<SendOutlined />} loading={loading} onClick={() => { void send(); }} style={{ height: 'auto' }} />
        </Space.Compact>
        <Text type="secondary" style={{ fontSize: 11 }}>{workflow ? 'Đề xuất chỉ ghi vào plan khi bạn bấm «Áp dụng vào plan…».' : 'Suggestions go into your draft — nothing changes until you press 💾 Save.'}</Text>
      </div>
    </div>
  );
};

export default FilmChatPanel;
